import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { devIssuesApi } from "@/features/dev_issues/api/devIssues";
import type { IssueCategory, IssuePriority, IssueStatus } from "@/features/dev_issues/types";

const keys = {
  all: ["dev-issues"] as const,
  list: (filters?: object) => ["dev-issues", "list", filters] as const,
  reporters: ["dev-issues", "reporters"] as const,
  assignees: ["dev-issues", "assignees"] as const,
  detail: (id: string) => ["dev-issues", id] as const,
  comments: (id: string) => ["dev-issues", id, "comments"] as const,
};

export function useDevIssues(filters?: { status?: IssueStatus; priority?: IssuePriority; category?: IssueCategory; title?: string; reporter?: string; assignee?: string; my_issues?: boolean; page?: number; ordering?: string; ordering_dir?: string }) {
  return useQuery({
    queryKey: keys.list(filters),
    queryFn: () => devIssuesApi.list(filters),
  });
}

export function useDevIssueReporters(enabled = true) {
  return useQuery({
    queryKey: keys.reporters,
    queryFn: () => devIssuesApi.listReporters(),
    enabled,
  });
}

export function useDevIssueAssignees(enabled = true) {
  return useQuery({
    queryKey: keys.assignees,
    queryFn: () => devIssuesApi.listAssignees(),
    enabled,
  });
}

export function useOpenDevIssuesCount(enabled = true) {
  return useQuery({
    queryKey: keys.list({ status: "open,in_progress" }),
    queryFn: () => devIssuesApi.list({ status: "open,in_progress" }),
    enabled,
    select: (data) => data.meta.count,
  });
}

export function useDevIssue(id: string) {
  return useQuery({
    queryKey: keys.detail(id),
    queryFn: () => devIssuesApi.get(id),
    enabled: !!id,
  });
}

export function useDevIssueComments(issueId: string) {
  return useQuery({
    queryKey: keys.comments(issueId),
    queryFn: () => devIssuesApi.listComments(issueId),
    enabled: !!issueId,
  });
}

export function useCreateDevIssue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { title: string; description: string }) => devIssuesApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.all }),
  });
}

export function useUpdateDevIssue(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<{ title: string; description: string; category: IssueCategory | null; priority: IssuePriority | null; status: IssueStatus }>) =>
      devIssuesApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      qc.invalidateQueries({ queryKey: keys.all });
    },
  });
}

export function useResolveDevIssue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => devIssuesApi.resolve(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      qc.invalidateQueries({ queryKey: keys.all });
    },
  });
}

export function useWontFixDevIssue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => devIssuesApi.wontFix(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      qc.invalidateQueries({ queryKey: keys.all });
    },
  });
}

export function useReopenDevIssue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => devIssuesApi.reopen(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      qc.invalidateQueries({ queryKey: keys.all });
    },
  });
}

export function useTakeDevIssue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => devIssuesApi.take(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      qc.invalidateQueries({ queryKey: keys.all });
    },
  });
}

export function useUnassignDevIssue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => devIssuesApi.unassign(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      qc.invalidateQueries({ queryKey: keys.all });
    },
  });
}

export function useAddDevIssueComment(issueId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ content, mentionIds = [] }: { content: string; mentionIds?: string[] }) =>
      devIssuesApi.addComment(issueId, content, mentionIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.comments(issueId) });
      // Mentions auto-promote to participants — refresh the issue so the
      // participants chips update without a manual reload.
      qc.invalidateQueries({ queryKey: keys.detail(issueId) });
    },
  });
}

export function useAddDevIssueParticipant(issueId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => devIssuesApi.addParticipant(issueId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.detail(issueId) });
      qc.invalidateQueries({ queryKey: keys.all });
    },
  });
}

export function useRemoveDevIssueParticipant(issueId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => devIssuesApi.removeParticipant(issueId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.detail(issueId) });
      qc.invalidateQueries({ queryKey: keys.all });
    },
  });
}

export function useUploadDevIssueAttachment(issueId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => devIssuesApi.uploadAttachment(issueId, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.detail(issueId) }),
  });
}

export function useUploadCommentAttachment(issueId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ commentId, file }: { commentId: string; file: File }) =>
      devIssuesApi.uploadCommentAttachment(issueId, commentId, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.comments(issueId) }),
  });
}

export function useDeleteDevIssue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => devIssuesApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.all }),
  });
}

export function useDeleteDevIssueAttachment(issueId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) => devIssuesApi.deleteAttachment(issueId, attachmentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.detail(issueId) }),
  });
}
